/**
 * homeHero.js — v3.7.1
 * Fixes TDZ error and keeps mobile phase 3‑4 to 50 px only on mobile.
 * Adds progressive scaling on mobile mode-3 transition.
 * Accelerates mode-4 fade-out on mobile.
 */
export default function () {
  const debug = true;
  if (debug) console.log('🔥 Hero Homepage Loaded v3.8');

  /* ------------------------------------------------------------------
   *  CONFIG
   * ----------------------------------------------------------------*/
  const initialHeightPercent   = 0.30;
  const mobileBreakpoint       = 1024;
  const smallScreenBreakpoint  = 1360;
  const smallMobileBreakpoint  = 480;   // <= 480 px

  /* ------------------------------------------------------------------
   *  DOM REFERENCES
   * ----------------------------------------------------------------*/
  const hero      = document.querySelector('.hero-homepage');
  const container = hero?.querySelector('.hero-homepage__container');
  const video     = hero?.querySelector('.hero-homepage__video');
  const content   = hero?.querySelector('.hero-homepage__content');
  const scrollToEl= hero?.querySelector('.hero-homepage__scroll-to');
  const spacer    = hero?.querySelector('.hero-homepage__spacer');

  if (!hero || !container || !video || !content || !spacer) return;

  /* ------------------------------------------------------------------
   *  VIEWPORT & DEVICE FLAGS  (declare BEFORE anything that uses them)
   * ----------------------------------------------------------------*/
  let viewportWidth  = window.innerWidth;
  let viewportHeight = window.innerHeight;

  const isMobile      = viewportWidth <  mobileBreakpoint;
  const isSmallMobile = viewportWidth <= smallMobileBreakpoint;
  const isSmallScreen = viewportWidth >= mobileBreakpoint &&
                        viewportWidth <= smallScreenBreakpoint;

  /* ------------------------------------------------------------------
   *  STATE
   * ----------------------------------------------------------------*/
  let currentMode      = 'mode-1';
  let scrollLocked     = false;
  let virtualScrollY   = 0;
  let userHasScrolled  = false;
  let overrideScrollY  = null;
  let lastScrollY      = window.scrollY;

  /* ------------------------------------------------------------------
   *  SCROLL DISTANCES
   * ----------------------------------------------------------------*/
  const triggerPoint           = hero.offsetTop;
  const scrollDistancePerPhase = 200;   // default for desktop / tablet
  const mobilePhaseDuration    = 50;    // mobile transitions

  // Calculate phase boundaries AFTER isMobile is known — fixes TDZ error
  const mode2End   = triggerPoint + scrollDistancePerPhase;
  const mode3End   = mode2End + (isMobile ? mobilePhaseDuration
                                          : scrollDistancePerPhase);
  const mode4End   = mode3End + (isMobile ? mobilePhaseDuration
                                          : scrollDistancePerPhase);
  const mode4Buffer = isMobile ? 0 : 100;   // only add buffer on desktop

  /* ------------------------------------------------------------------
   *  LOADER
   * ----------------------------------------------------------------*/
  const loaderGoesAway = 3000;
  const loader         = hero.querySelector('.hero-homepage__video-loader');

  /* ------------------------------------------------------------------
   *  INITIAL DIMENSIONS
   * ----------------------------------------------------------------*/
  let initialVideoHeight, initialVideoWidth;

  if (isSmallMobile) {
    // Very small mobile: keep 16:9 and some side padding
    initialVideoWidth  = viewportWidth - 40;
    initialVideoHeight = Math.round((initialVideoWidth * 9) / 16);

    setTimeout(() => {
      const iframe = video.querySelector('iframe');
      if (iframe) {
        iframe.style.aspectRatio = '16/9';
        iframe.style.height      = 'auto';
        iframe.style.position    = 'relative';
        iframe.style.display     = 'block';
        iframe.style.borderRadius= '20px';
        video.style.height       = 'auto';
        video.style.minHeight    = 'unset';
        video.style.margin       = '0 auto';
      }
    }, 100);
  } else if (isMobile) {
    initialVideoHeight = Math.round(viewportHeight * initialHeightPercent);
    initialVideoWidth  = video.offsetWidth;
  } else if (isSmallScreen) {
    initialVideoHeight = video.offsetHeight || Math.round(viewportHeight * 0.5);
    initialVideoWidth  = Math.min(viewportWidth - 40, 1300);
  } else {
    initialVideoHeight = video.offsetHeight;
    initialVideoWidth  = video.offsetWidth;
  }

  const rowGap = parseFloat(getComputedStyle(container).rowGap) || 0;

  /* ------------------------------------------------------------------
   *  INITIAL LAYOUT TWEAKS
   * ----------------------------------------------------------------*/
  video.style.opacity  = '1';
  spacer.style.height  = `${initialVideoHeight}px`;
  content.style.marginTop = `${initialVideoHeight + rowGap}px`;

  setTimeout(() => content.style.opacity = '1', 0);
  setTimeout(() => { if (scrollToEl) scrollToEl.style.opacity = '1'; }, 2500);
  setTimeout(() => {
    if (loader && loader.parentNode) loader.parentNode.removeChild(loader);
  }, loaderGoesAway);

  if (isSmallMobile) {
    video.style.width  = `${initialVideoWidth}px`;
    video.style.height = 'auto';
    const iframe = video.querySelector('iframe');
    if (iframe) {
      iframe.style.aspectRatio = '16/9';
      iframe.style.height      = 'auto';
    }
  } else if (isMobile) {
    video.style.height = `${initialVideoHeight}px`;
  } else if (isSmallScreen) {
    video.style.width  = `${initialVideoWidth}px`;
    video.style.height = `${initialVideoHeight}px`;
  }

  /* ------------------------------------------------------------------
   *  UTILS
   * ----------------------------------------------------------------*/
  function updateMode (mode) {
    if (currentMode !== mode) {
      currentMode = mode;
      container.setAttribute('data-mode', mode);
      if (debug) console.log(`Switched to ${mode}`);
    }
  }

  /* ------------------------------------------------------------------
   *  VIRTUAL SCROLL MAIN LOOP
   * ----------------------------------------------------------------*/
  function processVirtualScroll () {
    if (!userHasScrolled) return;

    const currentIsSmallMobile = viewportWidth <= smallMobileBreakpoint;

    /* ---------- MODE 1 ------------------------------------------------*/
    if (virtualScrollY < triggerPoint) {
      updateMode('mode-1');
      document.documentElement.style.setProperty('--hero-video-width',
                                                 `${initialVideoWidth}px`);

      if (currentIsSmallMobile) {
        resetInlineStylesMobile ();
        video.style.position = 'absolute';
        video.style.width    = `${initialVideoWidth}px`;
        video.style.height   = 'auto';
        const iframe = video.querySelector('iframe');
        if (iframe) {
          iframe.style.aspectRatio = '16/9';
          iframe.style.height      = 'auto';
          iframe.style.display     = 'block';
        }
      } else {
        video.style.position = 'absolute';
        video.style.height   = `${initialVideoHeight}px`;
        if (viewportWidth >= mobileBreakpoint && viewportWidth <= smallScreenBreakpoint) {
          video.style.width = `${initialVideoWidth}px`;
        }
      }

      video.style.left   = '50%';
      video.style.transform = 'translateX(-50%)';
      video.style.opacity   = '1';
      spacer.style.height   = `${initialVideoHeight}px`;
      content.style.marginTop =
        `${initialVideoHeight + (currentIsSmallMobile ? 20 : rowGap)}px`;
      return;
    }

    /* ---------- MODE 2 ------------------------------------------------*/
    if (virtualScrollY >= triggerPoint && virtualScrollY < mode2End) {
      updateMode('mode-2');
      const progress = (virtualScrollY - triggerPoint) / (mode2End - triggerPoint);

      if (currentIsSmallMobile) {
        resetInlineStylesMobile ();
        const newWidth = initialVideoWidth +
                         (viewportWidth - initialVideoWidth) * progress;
        video.style.width  = `${newWidth}px`;
        video.style.height = 'auto';

        const iframe = video.querySelector('iframe');
        if (iframe) {
          iframe.style.aspectRatio = '16/9';
          iframe.style.width       = '100%';
          iframe.style.height      = 'auto';
          iframe.style.position    = 'relative';
          iframe.style.display     = 'block';
          iframe.style.visibility  = 'visible';
          iframe.style.opacity     = '1';
          iframe.style.margin      = '0 auto';
        }

        video.style.position  = 'fixed';
        video.style.top       = '0';
        video.style.left      = '50%';
        video.style.transform = 'translateX(-50%)';
        video.style.opacity   = '1';
        video.style.zIndex    = '99';
      } else {
        const newWidth = initialVideoWidth +
                         (viewportWidth - initialVideoWidth) * progress;
        video.style.width  = `${newWidth}px`;
        video.style.height = `${initialVideoHeight}px`;
      }

      video.style.top     = '0';
      video.style.left    = '50%';
      video.style.transform = 'translateX(-50%)';
      video.style.position = 'fixed';
      spacer.style.height  = `${initialVideoHeight}px`;
      return;
    }

    /* ---------- MODE 3 ------------------------------------------------*/
    if (virtualScrollY >= mode2End && virtualScrollY < mode4End + mode4Buffer) {
      content.style.opacity = '1';   // Make sure content is visible

      updateMode('mode-3');
      const scrollProgress = Math.min(1,
        Math.max(0, (window.scrollY - mode2End) / ((mode4End + mode4Buffer) - mode2End))
      );

      if (currentIsSmallMobile) {
        // Calculate a progressive height value based on scroll progress
        const newHeight = initialVideoHeight + 
                         (viewportHeight - initialVideoHeight) * scrollProgress;
                         
        video.style.width      = `${viewportWidth}px`;
        video.style.height     = `${Math.min(newHeight, viewportHeight)}px`;
        video.style.position   = 'fixed';
        video.style.top        = '0';
        video.style.left       = '0';
        video.style.transform  = 'none';
        video.style.margin     = '0';
        video.style.opacity    = '1';
        video.style.borderRadius = '0';
        video.style.zIndex     = '99';

        const iframe = video.querySelector('iframe');
        if (iframe) {
          iframe.style.width  = 'revert-layer';
          iframe.style.height = 'revert-layer';
          iframe.style.setProperty('--h', '100vh');
          iframe.style.position = 'absolute';
          iframe.style.top    = '0';
          iframe.style.left   = '0';
          iframe.style.aspectRatio = 'unset';
          iframe.style.borderRadius = '0';
        }
      } else {
        const newHeight = initialVideoHeight +
                          (viewportHeight - initialVideoHeight) * scrollProgress;
        video.style.width  = `${viewportWidth}px`;
        video.style.height = `${Math.min(newHeight, viewportHeight)}px`;
      }

      if (scrollProgress > 0.5) {
        spacer.style.height   = `${viewportHeight}px`;
        content.style.marginTop = `${viewportHeight + rowGap}px`;
      }
      return;
    }

    /* ---------- MODE 4 ------------------------------------------------*/
    if (virtualScrollY >= mode4End + mode4Buffer) {
      updateMode('mode-4');

      if (currentIsSmallMobile) {
        video.style.width  = `${viewportWidth}px`;
        video.style.height = `${viewportHeight}px`;
        video.style.position = 'fixed';
        video.style.top    = '0';
        video.style.left   = '0';
        video.style.borderRadius = '0';
        video.style.zIndex = '99';

        const iframe = video.querySelector('iframe');
        if (iframe) {
          iframe.style.width  = 'revert-layer';
          iframe.style.height = '100%';
          iframe.style.setProperty('--h', '100vh');
          iframe.style.position = 'absolute';
          iframe.style.top    = '0';
          iframe.style.left   = '0';
          iframe.style.borderRadius = '0';
        }
        
        /* ---------------------------------------------------------------
        * Mobile: fade out more gently
        * - Needs ~80 % of the viewport to reach opacity 0
        * - easeOutQuad(t) slows the first half of the fade
        * ------------------------------------------------------------- */
        const fadeRange      = viewportHeight * 1;  // ≈ 600‑700 px on most phones
        const travelled      = Math.max(0, virtualScrollY - (mode4End + mode4Buffer));
        const t              = Math.min(1, travelled / fadeRange); // 0 → 1
        const easeOutQuad    = x => x * (2 - x);       // classic easing
        const fadeProgress   = easeOutQuad(t);         // slower start, faster end

        video.style.opacity  = `${1 - fadeProgress}`;

        /* -----------------------------------------------------------
         * NEW • show/hide content only in mobile mode‑4
         * --------------------------------------------------------- */
        if (fadeProgress < 0.95) {
          content.style.opacity = '0';   // hide until fade almost done
          spacer.style.height   = `${viewportHeight}px`;
        } else {
          content.style.opacity = '1';   // reveal
          spacer.style.height   = '80px';// gives a touch of space under hero
        }
      } else {
        // Desktop handling (unchanged)
        video.style.height = `${viewportHeight}px`;
        
        spacer.style.height   = `${viewportHeight}px`;
        content.style.marginTop = `${viewportHeight + rowGap}px`;

        const rect         = spacer.getBoundingClientRect();
        const fadeStart    = 0;
        const fadeEnd      = -viewportHeight * 0.25;
        const fadeProgress = Math.max(0,
          Math.min(1, 1 - (rect.top - fadeEnd) / (fadeStart - fadeEnd))
        );
        
        video.style.opacity = `${1 - fadeProgress}`;
      }
      
      // These need to happen regardless of device
      if (!currentIsSmallMobile) {
        spacer.style.height   = `${viewportHeight}px`;
        content.style.marginTop = `${viewportHeight + rowGap}px`;
      }
    }
  }

  /* ------------------------------------------------------------------
   *  EVENTS
   * ----------------------------------------------------------------*/
  function onScroll () {
    // Check for bypass flag before applying scroll lock
    if (window._heroScrollBypass) {
      // When navigation is controlling the scroll, don't interfere
      return;
    }
    
    if (!scrollLocked) virtualScrollY = window.scrollY;
    if (overrideScrollY !== null) virtualScrollY = overrideScrollY;
    if (virtualScrollY >= triggerPoint) userHasScrolled = true;
    processVirtualScroll();
  }

  window.addEventListener('scroll', onScroll);

  window.addEventListener('resize', () => {
    viewportWidth  = window.innerWidth;
    viewportHeight = window.innerHeight;
    const currentIsSmallMobile = viewportWidth <= smallMobileBreakpoint;

    if (currentIsSmallMobile && currentMode === 'mode-1') {
      const newWidth  = viewportWidth - 40;
      const newHeight = (newWidth * 9) / 16;
      video.style.width  = `${newWidth}px`;
      video.style.height = 'auto';

      const iframe = video.querySelector('iframe');
      if (iframe) {
        iframe.style.aspectRatio = '16/9';
        iframe.style.height      = 'auto';
      }

      spacer.style.height  = `${newHeight}px`;
      content.style.marginTop = `${newHeight + 20}px`;
    } else if (viewportWidth >= mobileBreakpoint &&
               viewportWidth <= smallScreenBreakpoint &&
               currentMode === 'mode-1') {
      video.style.width = `${Math.min(viewportWidth - 40, 1300)}px`;
    }

    processVirtualScroll();
  });

  /* Kick‑off once on load */
  processVirtualScroll();

  /* ------------------------------------------------------------------
   *  SECTION FADE‑IN (unchanged)
   * ----------------------------------------------------------------*/
  function fadeInSections () {
    if (debug) console.log('Fading in the sections within <main>...');
    const modules  = document.querySelectorAll('#content > .l-module');
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.style.opacity = '1';
            obs.unobserve(entry.target);
          }, 1000);
        }
      });
    }, { threshold : 0.1 });

    modules.forEach(module => {
      module.style.opacity = '0';
      observer.observe(module);
    });
  }
  fadeInSections();

  /* ------------------------------------------------------------------
   *  HELPER — clears fullscreen inline styles when leaving mode3/4
   * ----------------------------------------------------------------*/
  function resetInlineStylesMobile () {
    if (!isMobile) return;            // desktop/tablet untouched
    video.removeAttribute('style');
    const iframe = video.querySelector('iframe');
    if (iframe) iframe.removeAttribute('style');
  }
}
